<script setup lang="ts">
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Label,
    Textarea,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhSystemLabel,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, formatAgo, type PendingRecommendation } from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import { useClientTable } from '@/composables/useClientTable'
import { useListState } from '@/composables/useListState'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { addressLabel } from '@/lib/presentation'

/** A pending recommendation as the table shows it: the task it improves, how sure it is, and how long it has sat. */
type RecommendationRow = PendingRecommendation & { taskLabel: string; raised: string; confidencePercent: string }

/** A decided recommendation: what was decided, where the work went, and who decided it. */
type DecidedRow = PendingRecommendation & { taskLabel: string; decided: string; decidedByLabel: string }

const recommendations = ref<PendingRecommendation[]>([])
const decidedRecommendations = ref<PendingRecommendation[]>([])
const error = ref<string | null>(null)
const busy = ref<string | null>(null)
const loading = ref(true)
/** The row being declined: Decline opens a dialog that asks why before it sends. */
const declining = ref<PendingRecommendation | null>(null)
const reason = ref('')
/** Ticks every second so "raised" stays live. */
const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null

const taskLabel = (row: PendingRecommendation) => row.taskName ?? row.taskSlug ?? row.taskId

const rows = computed<RecommendationRow[]>(() =>
    recommendations.value.map(recommendation => ({
        ...recommendation,
        taskLabel: taskLabel(recommendation),
        raised: formatAgo(now.value - new Date(recommendation.createdAt).getTime()),
        confidencePercent: `${Math.round(recommendation.confidence * 100)}%`,
    })),
)

const decidedRows = computed<DecidedRow[]>(() =>
    decidedRecommendations.value.map(recommendation => ({
        ...recommendation,
        taskLabel: taskLabel(recommendation),
        decided: recommendation.decidedAt ? formatAgo(now.value - new Date(recommendation.decidedAt).getTime()) : '—',
        decidedByLabel: recommendation.decidedRunId ? 'Triage run' : 'A person',
    })),
)

const table = useClientTable({
    data: rows,
    searchFields: ['taskLabel', 'title', 'summary'],
    defaultSort: 'confidence',
    defaultOrder: 'DESC',
})

const decidedTable = useClientTable({
    data: decidedRows,
    searchFields: ['taskLabel', 'title', 'decisionNote'],
    defaultSort: 'decidedAt',
    defaultOrder: 'DESC',
})
/** #633: each table's search, sort and filters come back on the next visit, each under its own key. */
const list = useListState('self-improvement', table.listFields)
const decidedList = useListState('self-improvement-decided', decidedTable.listFields)

const columns: DataTableColumn[] = [
    { key: 'taskLabel', label: 'Task', sort: 'taskLabel', type: 'text' },
    { key: 'title', label: 'Recommendation', sort: 'title', type: 'text', class: 'max-w-sm' },
    { key: 'confidencePercent', label: 'Confidence', sort: 'confidence', filterable: false },
    { key: 'raised', label: 'Raised', sort: 'createdAt', filterable: false },
    { key: 'actions', label: 'Actions', filterable: false, class: 'text-right' },
]

const decidedColumns: DataTableColumn[] = [
    { key: 'taskLabel', label: 'Task', sort: 'taskLabel', type: 'text' },
    { key: 'title', label: 'Recommendation', sort: 'title', type: 'text', class: 'max-w-sm' },
    { key: 'status', label: 'Decision', sort: 'status', type: 'text' },
    { key: 'lane', label: 'Lane', sort: 'lane', type: 'text' },
    { key: 'decisionNote', label: 'Reason', type: 'text', filterable: false, class: 'max-w-sm' },
    { key: 'decidedByLabel', label: 'Decided by', filterable: false },
    { key: 'decided', label: 'Decided', sort: 'decidedAt', filterable: false },
]

onMounted(() => {
    void load()
    timer = setInterval(() => {
        now.value = Date.now()
        void load()
    }, 5000)
})

onUnmounted(() => {
    if (timer) {
        clearInterval(timer)
    }
})

async function load(): Promise<void> {
    try {
        const [pending, decided] = await Promise.all([api.recommendations(), api.decidedRecommendations()])
        recommendations.value = pending
        decidedRecommendations.value = decided
        error.value = null
    } catch {
        error.value = 'Could not load recommendations.'
    } finally {
        loading.value = false
    }
}

/** Approving starts the implement run; declining keeps the row with the reason. Either way it leaves the pending table. */
async function decide(id: string, decision: 'approve' | 'decline', why?: string): Promise<void> {
    if (
        busy.value ||
        (decision === 'approve' && recommendations.value.find(row => row.id === id)?.canImplement !== true)
    )
        return
    // #1796: approving starts the implement run, which is model work, so it asks for the AI account first. Declining
    // starts nothing and never asks.
    if (decision === 'approve' && !(await requireAiAccount())) return
    busy.value = id
    try {
        await api.decideRecommendation(id, decision, why)
        declining.value = null
        reason.value = ''
        await load()
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not ${decision} (${caught.message}).` : `Could not ${decision}.`
    } finally {
        busy.value = null
    }
}

function startDecline(row: PendingRecommendation): void {
    declining.value = row
    reason.value = ''
}

/** Closing the dialog, by Cancel, Escape or the backdrop, drops the half-written reason with it. */
function cancelDecline(): void {
    declining.value = null
    reason.value = ''
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle>Self-Improvement</ZhPageTitle>
		<p class="text-muted-foreground text-sm">
			After a run of a task your enterprise owns, <code>task-self-improvement</code> may propose a change to its definition. Nothing
			here is blocking. Triage decides each one: decline it with a reason (it is kept, so the same change is not
			proposed again), action it with the GitHub issue that takes the work, or approve it to start the implement
			run, which saves a new task version if the change holds (it can never widen tools, drop gates or raise
			spend). Open a row for the full write-up.
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<Dialog :open="declining !== null" @update:open="open => { if (!open) cancelDecline() }">
			<DialogContent v-if="declining" class="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Why decline “{{ declining.title }}”?</DialogTitle>
					<DialogDescription>
						The recommendation is kept with your reason, so the same change is not proposed again.
					</DialogDescription>
				</DialogHeader>
				<div class="grid gap-2">
					<Label for="self-improvement-decline-reason">Reason</Label>
					<Textarea
						id="self-improvement-decline-reason"
						v-model="reason"
						rows="3"
						placeholder="For example: not reproduced in the source run, or already fixed"
					/>
				</div>
				<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
				<DialogFooter>
					<Button type="button" size="sm" variant="outline" @click="cancelDecline">Cancel</Button>
					<Button
						type="button"
						size="sm"
						:disabled="busy === declining.id || !reason.trim()"
						@click="decide(declining.id, 'decline', reason.trim())"
					>
						Decline
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
		<ZhEmptyState v-if="!loading && !recommendations.length && !error">
			No pending recommendations. Tasks are running on their current versions. Improvements your team proposes
			to its tasks appear here as recommendations.
			<template #action>
				<Button as-child size="sm"><RouterLink to="/tasks">Go to Tasks</RouterLink></Button>
			</template>
		</ZhEmptyState>
		<ZhPanel v-else flush>
			<DataTable
				storage-key="self-improvement"
				:columns="columns"
				:loading="loading"
				:list-state="list"
				v-bind="table.tableProps.value"
			>
				<template #cell-taskLabel="{ row, value }">
					<RouterLink class="font-medium underline" :to="`/tasks/${row.taskId}`">{{ value }}</RouterLink>
				</template>
				<template #cell-title="{ row, value }">
					<span class="flex flex-col gap-1">
						<RouterLink
							class="font-medium underline"
							:to="`/tasks/${row.taskId}/recommendations/${row.id}`"
						>
							{{ value }}
						</RouterLink>
						<span class="text-muted-foreground text-sm">{{ row.summary }}</span>
					</span>
				</template>
				<template #cell-confidencePercent="{ value }">
					<span class="tabular-nums">{{ value }}</span>
				</template>
				<template #cell-raised="{ row, value }">
					<ZhTooltip :text="new Date(String(row.createdAt)).toLocaleString()"><span class="tabular-nums">{{ value }}</span></ZhTooltip>
				</template>
				<template #cell-actions="{ row }">
					<span class="flex justify-end gap-2">
						<ZhTooltip
							:text="row.canImplement === true ? null : 'Only the owning enterprise can implement this change. Copy the task to create your own editable version.'"
						>
							<Button
								type="button"
								size="sm"
								:disabled="Boolean(busy) || row.canImplement !== true"
								@click.stop="decide(String(row.id), 'approve')"
							>
								Approve
							</Button>
						</ZhTooltip>
						<Button
							type="button"
							size="sm"
							variant="outline"
							:disabled="busy === row.id"
							@click.stop="startDecline(row as unknown as PendingRecommendation)"
						>
							Decline
						</Button>
					</span>
				</template>
			</DataTable>
		</ZhPanel>

		<ZhPanel v-if="decidedRecommendations.length" class="space-y-2">
			<ZhSectionHeading>Decided in the last 60 days</ZhSectionHeading>
			<DataTable
				storage-key="self-improvement-decided"
				:columns="decidedColumns"
				:loading="loading"
				:list-state="decidedList"
				v-bind="decidedTable.tableProps.value"
			>
				<template #cell-taskLabel="{ row, value }">
					<RouterLink class="font-medium underline" :to="`/tasks/${row.taskId}`">{{ value }}</RouterLink>
				</template>
				<template #cell-title="{ row, value }">
					<RouterLink class="underline" :to="`/tasks/${row.taskId}/recommendations/${row.id}`">
						{{ value }}
					</RouterLink>
				</template>
				<template #cell-status="{ row, value }">
					<span class="flex flex-col items-start gap-1">
						<ZhSystemLabel as="span">{{ value }}</ZhSystemLabel>
						<a v-if="row.issueUrl" class="underline" :href="String(row.issueUrl)" target="_blank" rel="noopener">
							{{ addressLabel(String(row.issueUrl)) }}
						</a>
					</span>
				</template>
				<template #cell-lane="{ value }">
					<!-- The same "—" DataTable draws for a cell with no value, so a recommendation with no lane reads as before. -->
					<ZhSystemLabel v-if="value != null" as="span">{{ value }}</ZhSystemLabel>
					<span v-else>—</span>
				</template>
				<template #cell-decisionNote="{ row, value }">
					<span class="text-sm">{{ value || row.errorMessage || '—' }}</span>
				</template>
				<template #cell-decidedByLabel="{ row, value }">
					<RouterLink v-if="row.decidedRunId" class="underline" :to="`/runs/${row.decidedRunId}`">
						{{ value }}
					</RouterLink>
					<span v-else>{{ value }}</span>
				</template>
			</DataTable>
		</ZhPanel>
	</section>
</template>
